import { isValidPhone, PHONE_DIGITS } from "./format";

export const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const isEmpty = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && v.trim() === "");

const errorClass = (el) =>
  el instanceof HTMLSelectElement ? "select--error" : "input--error";

const getField = (form, name) => {
  if (!form || !name) return null;
  const el = form.elements.namedItem(name);
  if (!el || el instanceof RadioNodeList) return null;
  return el;
};

const markInvalid = (form, name, message) => {
  const el = name ? getField(form, name) : null;
  if (!el) return;
  el.setCustomValidity(message || "Invalid value");
  el.classList.add(errorClass(el));
};

/**
 * Mark empty required fields with a red border. Returns true when valid.
 */
export const validateRequiredForm = (form) => {
  if (!(form instanceof HTMLFormElement)) return false;

  form.classList.add("was-validated");

  let valid = true;
  form.querySelectorAll("input, select, textarea").forEach((el) => {
    if (!el.willValidate) return;
    if (!el.validity.customError) el.setCustomValidity("");
    if (!el.checkValidity()) {
      el.classList.add(errorClass(el));
      valid = false;
    } else {
      el.classList.remove(errorClass(el));
    }
  });

  if (!valid) {
    form.querySelector(".input--error, .select--error, :invalid")?.focus?.();
  }

  return valid;
};

/**
 * Validate form fields and mark failures with a red border.
 *
 * Each rule: { value, name, label, required?, type?, minLength?, custom? }
 */
export const validateForm = (form, rules = []) => {
  if (!(form instanceof HTMLFormElement)) return false;

  form.classList.add("was-validated");

  const ruledNames = new Set(rules.map((r) => r.name).filter(Boolean));
  for (const name of ruledNames) {
    const el = getField(form, name);
    if (el) {
      el.setCustomValidity("");
      el.classList.remove(errorClass(el));
    }
  }

  let valid = true;

  for (const rule of rules) {
    const { value, name, label, required, type, minLength, custom } = rule;
    let msg = null;

    if (required && isEmpty(value)) {
      msg = `${label} is required`;
    }

    if (!msg && typeof custom === "function") {
      const customMsg = custom(value);
      if (customMsg) msg = customMsg;
    }

    if (!msg && !isEmpty(value)) {
      if (type === "email" && !isValidEmail(value)) {
        msg = `Please enter a valid ${label.toLowerCase()}`;
      } else if (type === "auMobile" && !isValidPhone(value, { required: !!required })) {
        msg = required
          ? `Enter ${label.toLowerCase()} (${PHONE_DIGITS} digits)`
          : `Enter a valid ${label.toLowerCase()} (${PHONE_DIGITS} digits) or leave blank`;
      } else if (minLength && String(value).trim().length < minLength) {
        msg = `${label} must be at least ${minLength} characters`;
      }
    }

    if (msg) {
      if (name) markInvalid(form, name, msg);
      valid = false;
    }
  }

  form.querySelectorAll("input, select, textarea").forEach((el) => {
    if (!el.willValidate) return;
    if (ruledNames.has(el.name)) {
      if (el.classList.contains(errorClass(el))) valid = false;
      return;
    }
    if (!el.checkValidity()) {
      el.classList.add(errorClass(el));
      valid = false;
    } else {
      el.classList.remove(errorClass(el));
    }
  });

  if (!valid) {
    form.querySelector(".input--error, .select--error, :invalid")?.focus?.();
  }

  return valid;
};

export default validateForm;
